import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';

const endpoint = '/api/v1/saas/platform/retry-settings';
const scheduleFields = [
  'ingestion_retry_delays_minutes',
  'ai_json_retry_delays_minutes',
  'postprocessing_retry_delays_minutes'
];
const scheduleSections = [
  { key: scheduleFields[0], title: 'ingestion_title', description: 'ingestion_description' },
  { key: scheduleFields[1], title: 'ai_json_title', description: 'ai_json_description' },
  { key: scheduleFields[2], title: 'postprocessing_title', description: 'postprocessing_description' }
];

const isValidSettings = settings => {
  if (!settings) return false;
  const schedulesValid = scheduleFields.every(key => {
    const delays = settings[key];
    return Array.isArray(delays)
      && delays.length === 4
      && delays.every(value => value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 43200)
      && delays.every((value, index) => index === 0 || Number(value) >= Number(delays[index - 1]));
  });
  const retries = Number(settings.ai_transient_max_retries);
  const baseDelay = Number(settings.ai_transient_retry_base_delay_seconds);
  const maxDelay = Number(settings.ai_transient_retry_max_delay_seconds);
  const scalarFields = [
    settings.ai_transient_max_retries,
    settings.ai_transient_retry_base_delay_seconds,
    settings.ai_transient_retry_max_delay_seconds
  ];
  return schedulesValid
    && scalarFields.every(value => value != null && String(value).trim() !== '')
    && Number.isInteger(retries) && retries >= 0 && retries <= 10
    && Number.isFinite(baseDelay) && baseDelay >= 0 && baseDelay <= 300
    && Number.isFinite(maxDelay) && maxDelay >= baseDelay && maxDelay <= 300;
};

const PlatformRetrySettings = () => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDraft(await apiRequest(endpoint));
    } catch {
      setError('saas.platform_retries.load_error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSchedule = (key, index, value) => {
    setDraft(current => ({
      ...current,
      [key]: current[key].map((delay, delayIndex) => delayIndex === index ? value : delay)
    }));
    setSaved(false);
    setError('');
  };

  const updateScalar = (key, value) => {
    setDraft(current => ({ ...current, [key]: value }));
    setSaved(false);
    setError('');
  };

  const save = async event => {
    event.preventDefault();
    if (!isValidSettings(draft)) {
      setError('saas.platform_retries.validation_error');
      return;
    }
    setSaving(true);
    setError('');
    setSaved(false);
    const payload = Object.fromEntries(Object.entries(draft).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.map(Number) : Number(value)
    ]));
    try {
      setDraft(await apiRequest(endpoint, { method: 'PUT', body: JSON.stringify(payload) }));
      setSaved(true);
    } catch (err) {
      setError(err.code === 'invalid_retry_settings'
        ? 'saas.platform_retries.validation_error'
        : 'saas.platform_retries.save_error');
    } finally {
      setSaving(false);
    }
  };

  return <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
    <Stack component="form" aria-label={t('saas.platform_retries.title')} spacing={2} onSubmit={save}>
      <Typography component="h2" variant="h6">{t('saas.platform_retries.title')}</Typography>
      <Typography color="text.secondary">{t('saas.platform_retries.description')}</Typography>
      {loading && <LinearProgress aria-label={t('saas.platform_retries.loading')} />}
      {error && <Alert severity="error" role="alert">{t(error)}</Alert>}
      {saved && <Alert severity="success" role="status">{t('saas.platform_retries.saved')}</Alert>}
      {!loading && !draft && <Button onClick={load} sx={{ alignSelf: 'flex-start' }}>{t('saas.platform_retries.retry')}</Button>}
      {draft && <>
        {scheduleSections.map(section => <Box
          key={section.key}
          component="fieldset"
          sx={{ m: 0, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Typography component="legend" variant="subtitle1" sx={{ px: 0.75, fontWeight: 600 }}>
            {t(`saas.platform_retries.${section.title}`)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(`saas.platform_retries.${section.description}`)}
          </Typography>
          <Grid container spacing={1.5}>
            {draft[section.key].map((delay, index) => <Grid item xs={12} sm={3} key={`${section.key}-${index}`}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label={t('saas.platform_retries.delay_label', { attempt: index + 1 })}
                value={delay}
                inputProps={{ min: 0, max: 43200, step: 'any' }}
                disabled={saving}
                onChange={event => updateSchedule(section.key, index, event.target.value)}
              />
            </Grid>)}
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t('saas.platform_retries.delay_help')}
          </Typography>
        </Box>)}
        <Box component="fieldset" sx={{ m: 0, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography component="legend" variant="subtitle1" sx={{ px: 0.75, fontWeight: 600 }}>
            {t('saas.platform_retries.ai_transient_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('saas.platform_retries.ai_transient_description')}
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label={t('saas.platform_retries.ai_transient_max_retries')}
                value={draft.ai_transient_max_retries}
                inputProps={{ min: 0, max: 10, step: 1 }}
                disabled={saving}
                onChange={event => updateScalar('ai_transient_max_retries', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label={t('saas.platform_retries.ai_transient_base_delay')}
                value={draft.ai_transient_retry_base_delay_seconds}
                inputProps={{ min: 0, max: 300, step: 'any' }}
                disabled={saving}
                onChange={event => updateScalar('ai_transient_retry_base_delay_seconds', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label={t('saas.platform_retries.ai_transient_max_delay')}
                value={draft.ai_transient_retry_max_delay_seconds}
                inputProps={{ min: 0, max: 300, step: 'any' }}
                disabled={saving}
                onChange={event => updateScalar('ai_transient_retry_max_delay_seconds', event.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t(saving ? 'saas.platform_retries.saving' : 'saas.platform_retries.save')}
        </Button>
      </>}
    </Stack>
  </Paper>;
};

export default PlatformRetrySettings;
